// Plugin de Vue para el sistema de roles
// Permite usar this.$roles en cualquier componente sin necesidad de importar el mixin

const RolesPlugin = {
  install(Vue, options) {
    // Agregar propiedades globales para roles
    Vue.prototype.$roles = {
      // Obtener todos los roles del usuario actual
      getUserRoles() {
        return Vue.prototype.$store.getters.userRoles
      },
      
      // Verificar si el usuario tiene un rol específico
      hasRole(roleName) {
        return Vue.prototype.$store.getters.hasRole(roleName)
      },
      
      // Verificar si el usuario tiene alguno de los roles especificados
      hasAnyRole(roleNames) {
        return Vue.prototype.$store.getters.hasAnyRole(roleNames)
      },
      
      // Verificar si el usuario tiene todos los roles especificados
      hasAllRoles(roleNames) {
        const userRoles = this.getUserRoles()
        if (!userRoles || !roleNames || roleNames.length === 0) return false
        
        return roleNames.every(roleName =>
          userRoles.some(role => role.nombre === roleName && role.activo)
        )
      },
      
      // Obtener nombres de roles activos
      getActiveRoleNames() {
        return this.getUserRoles()
          .filter(role => role.activo)
          .map(role => role.nombre)
      },
      
      // Obtener el primer rol activo del usuario
      getPrimaryRole() {
        const activeRoles = this.getUserRoles().filter(role => role.activo)
        return activeRoles.length > 0 ? activeRoles[0].nombre : null
      },
      
      // Verificar si el usuario es administrador
      isAdmin() {
        return this.hasRole('Administrador') || this.hasRole('Super Administrador')
      },
      
      // Verificar si el usuario tiene permisos de gestión
      isManager() {
        return this.hasAnyRole(['Gerente', 'Supervisor', 'Administrador', 'Super Administrador'])
      },
      
      // Contar roles activos
      getActiveRolesCount() {
        return this.getUserRoles().filter(role => role.activo).length
      },
      
      // Verificar si el usuario tiene al menos un rol activo
      hasActiveRoles() {
        return this.getActiveRolesCount() > 0
      }
    }

    // Directiva personalizada para mostrar/ocultar elementos basado en roles
    Vue.directive('role', {
      bind(el, binding, vnode) {
        const roles = binding.value
        const hasPermission = checkRolePermission(roles, vnode.context.$store)
        
        if (!hasPermission) {
          el.style.display = 'none'
        }
      },
      
      update(el, binding, vnode) {
        const roles = binding.value
        const hasPermission = checkRolePermission(roles, vnode.context.$store)
        
        el.style.display = hasPermission ? '' : 'none'
      }
    })

    // Directiva para deshabilitar elementos basado en roles
    Vue.directive('role-disable', {
      bind(el, binding, vnode) {
        const roles = binding.value
        const hasPermission = checkRolePermission(roles, vnode.context.$store)
        
        if (!hasPermission) {
          el.disabled = true
          el.classList.add('v-btn--disabled')
        }
      },
      
      update(el, binding, vnode) {
        const roles = binding.value
        const hasPermission = checkRolePermission(roles, vnode.context.$store)
        
        if (hasPermission) {
          el.disabled = false
          el.classList.remove('v-btn--disabled')
        } else {
          el.disabled = true
          el.classList.add('v-btn--disabled')
        }
      }
    })
  }
}

// Función helper para verificar permisos
function checkRolePermission(roles, store) {
  if (!roles) return true
  
  if (typeof roles === 'string') {
    return store.getters.hasRole(roles)
  }
  
  if (Array.isArray(roles)) {
    return store.getters.hasAnyRole(roles)
  }
  
  if (typeof roles === 'object') {
    if (roles.any) {
      return store.getters.hasAnyRole(roles.any)
    }
    if (roles.all) {
      const userRoles = store.getters.userRoles
      return roles.all.every(roleName =>
        userRoles.some(role => role.nombre === roleName && role.activo)
      )
    }
  }
  
  return true
}

export default RolesPlugin
